import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Repeat, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { useAverageStays } from "@/hooks/use-population-insights";
import { useInsightsContext } from "@/contexts/insights-context";
import { useState, useMemo } from "react";

type SpeciesFilter = "all" | "dogs" | "cats" | "other";

const SPECIES_COLORS: Record<string, string> = {
  DOG: "hsl(var(--chart-1))",
  CAT: "hsl(var(--chart-2))",
  OTHER: "hsl(var(--chart-3))",
};

interface AverageStaysChartProps {
  days: number;
}

function StatCard({ label, value, testId }: { label: string; value: string | number; testId: string }) {
  return (
    <div className="flex flex-col p-3 rounded-md border" data-testid={testId}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold">{value}</span>
    </div>
  );
}

export function AverageStaysChart({ days }: AverageStaysChartProps) {
  const { selectedShelter, isSystemView } = useInsightsContext();
  const location = isSystemView ? undefined : selectedShelter;
  const [speciesFilter, setSpeciesFilter] = useState<SpeciesFilter>("all");
  const { data, isLoading, isError, refetch } = useAverageStays(days, location);

  const chartData = useMemo(() => {
    if (!data?.bySpecies) return [];

    const allSpecies = [
      { key: "DOG", label: "Dogs", data: data.bySpecies.DOG },
      { key: "CAT", label: "Cats", data: data.bySpecies.CAT },
      { key: "OTHER", label: "Other", data: data.bySpecies.OTHER },
    ];

    const filtered = speciesFilter === "all"
      ? allSpecies
      : allSpecies.filter((s) => s.key === { dogs: "DOG", cats: "CAT", other: "OTHER" }[speciesFilter]);

    return filtered.map((s) => ({
      species: s.label,
      averageStays: s.data?.averageStays || 0,
      totalAnimals: s.data?.totalAnimals || 0,
      totalStays: s.data?.totalStays || 0,
      fill: SPECIES_COLORS[s.key],
    }));
  }, [data, speciesFilter]);

  const summaryData = useMemo(() => {
    if (!data) return null;
    if (speciesFilter === "all") return data.overall;
    const key = { dogs: "DOG", cats: "CAT", other: "OTHER" }[speciesFilter] as string;
    return data.bySpecies[key] || data.overall;
  }, [data, speciesFilter]);

  return (
    <Card data-testid="card-average-stays">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Repeat className="h-5 w-5 text-muted-foreground" />
            Avg Stays Per Animal
          </CardTitle>
        </div>
        <Select value={speciesFilter} onValueChange={(v) => setSpeciesFilter(v as SpeciesFilter)}>
          <SelectTrigger className="w-[140px]" data-testid="select-species-average-stays">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Species</SelectItem>
            <SelectItem value="dogs">Dogs</SelectItem>
            <SelectItem value="cats">Cats</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-4" data-testid="loading-average-stays">
            <div className="flex gap-3">
              <Skeleton className="h-16 flex-1" />
              <Skeleton className="h-16 flex-1" />
              <Skeleton className="h-16 flex-1" />
            </div>
            <Skeleton className="h-[250px] w-full" />
          </div>
        )}
        {isError && (
          <Alert variant="destructive" data-testid="error-average-stays">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Failed to load average stays data</span>
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-average-stays">
                <RefreshCw className="h-3 w-3 mr-1" /> Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {data && data.overall.totalAnimals === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-average-stays">
            No stay data available yet.
          </div>
        )}
        {data && data.overall.totalAnimals > 0 && summaryData && (
          <>
            <div className="mb-4">
              <p className="text-xs text-muted-foreground mb-1" data-testid="text-avg-stays-stats-hint">
                All records within selected date range
              </p>
              <div className="flex flex-wrap gap-3">
                <StatCard label="Overall Average" value={`${summaryData.averageStays} stays`} testId="stat-overall-average-stays" />
                <StatCard label="Total Animals" value={summaryData.totalAnimals} testId="stat-total-animals" />
                <StatCard label="Total Stays" value={summaryData.totalStays} testId="stat-total-stays" />
              </div>
            </div>
            <div className="h-[250px]" data-testid="chart-average-stays">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ left: 10, right: 10, top: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="species" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} domain={[0, "auto"]} />
                  <Tooltip
                    formatter={(value: number, _name: string, props: any) => [
                      `${value} stays/animal (${props.payload.totalAnimals} animals, ${props.payload.totalStays} stays)`,
                      "Average Stays",
                    ]}
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      borderColor: "hsl(var(--border))",
                      borderRadius: "6px",
                      color: "hsl(var(--card-foreground))",
                    }}
                  />
                  <Bar dataKey="averageStays" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                    <LabelList dataKey="averageStays" position="top" fontSize={12} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
